import { BrowserModule } from '@angular/platform-browser';
import {
  DoBootstrap,
  ErrorHandler,
  Injector,
  NgModule,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';

import {
  OrderformModuleNgrx,
  WidgetsModule,
  WrapperComponent,
  MonitoringService,
  OrderformTrackingCollectorService,
  Wrapper2Component,
  getOrderformSubmitState,
  getOrderformConfig,
  OrderformFascade,
  OrderformLocalStorageService,
} from '@orderform/widgets';

import { Store, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { createCustomElement } from '@angular/elements';
import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localeSl from '@angular/common/locales/sl';
// Phase 2 languages
import localeTr from '@angular/common/locales/tr';
import localeRo from '@angular/common/locales/ro';
import localeSv from '@angular/common/locales/sv';
import localeEl from '@angular/common/locales/el';
import localeHu from '@angular/common/locales/hu';
import localeCs from '@angular/common/locales/cs';
import localeSk from '@angular/common/locales/sk';
import localeBg from '@angular/common/locales/bg';
import localeHr from '@angular/common/locales/hr';
import localeFi from '@angular/common/locales/fi';
import localeEt from '@angular/common/locales/et';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { WaitingService } from '../../../../libs/orderform-widgets/src/lib/services/waiting.service';
import { debounce, map, skipWhile, withLatestFrom } from 'rxjs/operators';
import { of, timer } from 'rxjs';
import { environment } from '../environments/environment';
import {
  InterfaceToastItemDefaultConfig,
  ToasterConfigToken,
  ToasterModule,
  ToasterService,
} from '@digistore/ds24-ui/toaster';
import { OrderformIframeService } from '../../../../libs/orderform-widgets/src/lib/services/orderform-iframe.service';
import { ApiService } from 'libs/orderform-widgets/src/lib/services/api.service';
import { InputsGeneratorElementComponent } from 'libs/orderform-widgets/src/lib/components/inputs-generator/inputs-generator-element.component';
import { ApmErrorHandler, APM } from '@elastic/apm-rum-angular';
import { apm } from '@elastic/apm-rum';
import {
  GrowthBookService,
  GrowthBookFeature,
} from '../../../../libs/orderform-widgets/src/lib/services/growthbook.service';
import { IframeModalService } from '../../../../libs/orderform-widgets/src/lib/components/iframe-modal/iframe-modal.service';
import { RecaptchaService } from '../../../../libs/orderform-widgets/src/lib/services/recaptcha.service';
import { OrderformSdkHostService } from '../../../../libs/orderform-widgets/src/lib/sdk/orderform-sdk-host.service';
import { extractUpgradeRouteParams } from '../../../../libs/orderform-widgets/src/lib/misc/upgrade-route-params';

registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeIt);
registerLocaleData(localeNl);
registerLocaleData(localePl);
registerLocaleData(localePt);
registerLocaleData(localeSl);
// Phase 2 languages
registerLocaleData(localeTr);
registerLocaleData(localeRo);
registerLocaleData(localeSv);
registerLocaleData(localeEl);
registerLocaleData(localeHu);
registerLocaleData(localeCs);
registerLocaleData(localeSk);
registerLocaleData(localeBg);
registerLocaleData(localeHr);
registerLocaleData(localeFi);
registerLocaleData(localeEt);

let orderformIframeServiceRef: OrderformIframeService;

const PURCHASE_CAUSE_UPGRADE = 'upgrade';

@NgModule({
  declarations: [],
  imports: [
    WidgetsModule,
    BrowserAnimationsModule,
    ReactiveFormsModule, // need to include there because we have an error on radio inputs
    BrowserModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot(),
    OrderformModuleNgrx,
    ToasterModule,
  ],
  providers: [
    {
      provide: APM,
      useValue: apm,
    },
    {
      provide: APP_BASE_HREF,
      useValue: '',
    },
    {
      provide: ToasterConfigToken,
      // Do not use factory because we have circular deps in iframeService...
      useValue: <InterfaceToastItemDefaultConfig>{
        close: true,
        duration: 100000,
        executeFunctionOnToast: (toastData) => {
          if (orderformIframeServiceRef) {
            orderformIframeServiceRef.scrollToIframeElementPosition(0);
          }
        },
      },
    },
    { provide: ErrorHandler, useClass: ApmErrorHandler },
    environment.providers,
    provideExperimentalZonelessChangeDetection(),
  ],
  bootstrap: [],
})
export class AppModule implements DoBootstrap {
  constructor(
    private _injector: Injector,
    private _apiService: ApiService,
    private _toasterService: ToasterService,
    private _waitingService: WaitingService,
    private _orderformIframeService: OrderformIframeService,
    private _store: Store<any>,
    private _monitoring: MonitoringService,
    private readonly growthBookService: GrowthBookService,
    private _trackingCollectorService: OrderformTrackingCollectorService,
    private _orderformFascade: OrderformFascade,
    private _localStorageService: OrderformLocalStorageService,
    private _iframeModalService: IframeModalService,
    private _recaptchaService: RecaptchaService,
    private _orderformSdkHost: OrderformSdkHostService
  ) {
    // API funktionen veröffentlichen
    (window as any).dsOrderform = this._apiService;
    (window as any).dsToast = this._toasterService; // for testing
    // dsOrderformReady is dispatched at the END of ngDoBootstrap, after all
    // customElements.define() calls. Dispatching here (constructor) would fire
    // before elements are defined, causing Vue widgets to insert Angular custom
    // elements that upgrade without a valid injector → runtime crash.

    // prevent public access via window
    orderformIframeServiceRef = this._orderformIframeService;
    (window as any).orderformIframeServiceRef = orderformIframeServiceRef;
    (window as any).wait = this._waitingService;

    // Expose IframeModalService for Vue widgets
    (window as any).angularIframeModalService = this._iframeModalService;

    // Initialize reCAPTCHA (hooks into beforeSubmit)
    this._recaptchaService.init();
    // Feed the shared SDK's read-only store view from the NgRx store.
    this._orderformSdkHost.init();
    // Extract upgrade route parameters and store them if this is an upgrade orderform
    const upgradeParams = extractUpgradeRouteParams();
    if (upgradeParams) {
      this._orderformFascade.setOrderStore({
        upgrade: upgradeParams,
        purchase_cause: PURCHASE_CAUSE_UPGRADE,
      });
    }

    this._store
      .select(getOrderformSubmitState)
      .pipe(
        skipWhile((submitState) => submitState === 'initial'),
        withLatestFrom(this._store.select(getOrderformConfig).pipe(map((config) => config.hide_process_popup))),
        debounce(([submitState]) => (submitState === 'pending' ? timer(300) : of(null)))
      )
      .subscribe(([submitState, hide]) => {
        switch (submitState) {
          case 'pending':
            this._waitingService.pending(hide);
            break;
          case 'success':
            this._waitingService.ready(hide);
            break;
          default:
            this._waitingService.hide();
        }
      });

    this._orderformIframeService.init();

    this.growthBookService.initialize().then(() => {
      if (
        !(
          window.location.pathname.startsWith('/product') ||
          window.location.pathname.startsWith('/checkout') ||
          window.location.pathname.startsWith('/offer')
        )
      ) {
        return;
      }
      const isFeatureEnabled = this.growthBookService.isFeatureEnabled(GrowthBookFeature.ORDERFORM_PROGRESS_SAVING);

      // Enable/disable localStorage service based on feature flag
      this._localStorageService.setEnabled(isFeatureEnabled);

      if (isFeatureEnabled) {
        // Load saved progress from localStorage
        this._orderformFascade.loadProgressFromLocalStorage();
      }
    });

    (async () => {
      if (document.querySelector('ds-orderform-plain-mode-toggle')) {
        const m = await import(
          '../../../../libs/orderform-widgets/src/lib/components/plain-mode-toggle/plain-mode-toggle.component'
        );
        m.PlainModeToggleComponent.defineCustomElement(this._injector);
      }
    })();

    this._monitoring.initialize();
    this._trackingCollectorService.initialize();

    // Apply CSS class based on reduced max-width feature flag
    this._orderformFascade.config$.pipe(map((config) => config.is_reduced_max_width)).subscribe((isReduced) => {
      if (isReduced) {
        document.body.classList.add('orderform-reduced-max-width');
      } else {
        document.body.classList.remove('orderform-reduced-max-width');
      }
    });
  }

  private createFooterComponents() {
    if (!(window as any).__dsOrderform_disable_footer) {
      const footerHTML =
        '<ds-orderform-wrapper component="Footer" visible-force="1"></ds-orderform-wrapper>' +
        '<ds-orderform-footer></ds-orderform-footer>';

      const notificationsComp = `
            <ds-orderform-wrapper component="Notifications" visible-force="1"></ds-orderform-wrapper>
            <ds-orderform-notifications></ds-orderform-notifications>`;
      /**
       * Wird im PGB-Editor benötigt und nur im Übersetzungseditor verwenden.
       * Um Widgets im Übersetzungstext anzeigen zu können (tinymce iframe + Hauptframe).
       */
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          document.body.insertAdjacentHTML('beforeend', footerHTML);
          document.body.insertAdjacentHTML('beforeend', notificationsComp);
        });
      } else {
        document.body.insertAdjacentHTML('beforeend', footerHTML);
        document.body.insertAdjacentHTML('beforeend', notificationsComp);
      }
    }
  }

  ngDoBootstrap() {
    const wrapperComponent = createCustomElement(WrapperComponent, {
      injector: this._injector,
    });
    customElements.define('ds-orderform-wrapper', wrapperComponent);

    const wrapper2Component = createCustomElement(Wrapper2Component, {
      injector: this._injector,
    });
    customElements.define('ds-orderform-payment-plan-changes', wrapper2Component);

    const wrapper2PaymentExpressCheckoutComponent = createCustomElement(
      class PaymentExpressCheckoutWrapper2 extends Wrapper2Component {},
      {
        injector: this._injector,
      }
    );
    customElements.define('ds-orderform-payment-express-checkout', wrapper2PaymentExpressCheckoutComponent);

    // Address-field renderer exposed for embedding inside Vue dialogs (e.g. the Payment
    // Express Checkout country/zip tax-calculation dialog).
    const inputsGeneratorElement = createCustomElement(InputsGeneratorElementComponent, {
      injector: this._injector,
    });
    customElements.define('ds-orderform-inputs-generator', inputsGeneratorElement);

    // Payment method renderers (lazy-loaded via Wrapper2)
    const paymentMethodTags = [
      'ds-payment-method-elv',
      'ds-payment-method-unzer',
      'ds-payment-method-umsbanking',
      'ds-payment-method-nmi',
      'ds-payment-method-stripe',
      'ds-payment-method-stripe-v2',
      'ds-payment-method-stripe-sepa-v2',
      'ds-payment-method-stripe-instant-bank-payments',
      'ds-payment-method-stripe-ideal',
      'ds-payment-method-stripe-wero',
      'ds-payment-method-heidelpay-cc',
      'ds-payment-method-braintree',
      'ds-payment-method-klarna',
      'ds-payment-method-primer',
      'ds-payment-method-primer-paypal',
      'ds-payment-method-primer-google-apple-pay',
      'ds-payment-method-whop',
      'ds-payment-method-unzer-paylater',
    ];
    for (const tag of paymentMethodTags) {
      const wrapper2Method = createCustomElement(class extends Wrapper2Component {}, { injector: this._injector });
      customElements.define(tag, wrapper2Method);
    }

    this.createFooterComponents();

    // Signal Vue PageBuilder widgets that Angular is fully bootstrapped and all
    // custom elements are defined. Must be last in ngDoBootstrap.
    window.dispatchEvent(new CustomEvent('dsOrderformReady'));
  }
}
